import type { MediaItem } from '@fdpf/shared';

export interface PrecacheProgress {
  albumId: string;
  done: number;
  total: number;
  failed: number;
  storedBytes: number;
}

export interface PrecacheResult extends PrecacheProgress {
  cancelled: boolean;
  quotaExceeded: boolean;
}

export interface CacheStats {
  totalBytes: number;
  entryCount: number;
  budgetBytes: number;
  /** 'auto' = derived from the browser's reported quota; 'manual' = explicitly set via setCacheBudget. */
  budgetSource?: 'auto' | 'manual';
  albums: Record<string, { bytes: number; count: number; lastAccessedAt?: number }>;
}

export function serviceWorkerSupported(): boolean {
  return 'serviceWorker' in navigator && typeof caches !== 'undefined';
}

let registration: ServiceWorkerRegistration | null = null;

export async function registerServiceWorker(): Promise<boolean> {
  if (!serviceWorkerSupported()) return false;
  try {
    // `updateViaCache: 'none'` forces the worker script itself to be revalidated on every check.
    // The default ('imports') lets an HTTP-cached sw.js pin a frame to an old build indefinitely —
    // and these devices are meant to run untouched for months.
    registration = await navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' });
    await navigator.serviceWorker.ready;
    return true;
  } catch {
    // A frame without a worker still works; it just pays for bandwidth on every loop.
    return false;
  }
}

function activeWorker(): ServiceWorker | null {
  return navigator.serviceWorker?.controller ?? registration?.active ?? null;
}

/** Request/response over a MessageChannel, since the worker has no return channel otherwise. */
function call<T>(message: unknown, timeoutMs = 15000): Promise<T> {
  return new Promise((resolve, reject) => {
    const worker = activeWorker();
    if (!worker) {
      reject(new Error('Service Worker 尚未接管本页面'));
      return;
    }

    const channel = new MessageChannel();
    const timer = setTimeout(() => {
      channel.port1.close();
      reject(new Error('Service Worker 响应超时'));
    }, timeoutMs);

    channel.port1.onmessage = (event) => {
      clearTimeout(timer);
      channel.port1.close();
      const data = event.data as { error?: string };
      if (data && data.error) reject(new Error(data.error));
      else resolve(event.data as T);
    };

    worker.postMessage(message, [channel.port2]);
  });
}

/**
 * The objects worth holding locally: the `display` copy of everything (that is what plays), plus a
 * video's `poster`. Thumbnails are deliberately excluded — on a 5000-photo album they would add
 * ~100MB for something only the album grid ever shows, and the grid caches them as it renders.
 */
export function precacheUrls(items: MediaItem[]): string[] {
  const urls: string[] = [];
  for (const item of items) {
    urls.push(`/m/${item.contentHash}/display`);
    if (item.kind === 'video') urls.push(`/m/${item.contentHash}/poster`);
  }
  return urls;
}

export function estimateBytes(items: MediaItem[]): number {
  return items.reduce((sum, item) => sum + (item.bytesDisplay || 0), 0);
}

export function startPrecache(albumId: string, items: MediaItem[]): Promise<{ started: boolean }> {
  return call<{ started: boolean }>({
    type: 'PRECACHE_ALBUM',
    albumId,
    urls: precacheUrls(items),
    estimatedBytes: estimateBytes(items),
  });
}

export function cancelPrecache(): Promise<void> {
  return call<void>({ type: 'CANCEL_PRECACHE' });
}

export function albumCacheStatus(items: MediaItem[]): Promise<{ cached: number; total: number }> {
  return call<{ cached: number; total: number }>({ type: 'ALBUM_STATUS', urls: precacheUrls(items) });
}

export function cacheStats(): Promise<CacheStats> {
  return call<CacheStats>({ type: 'CACHE_STATS' });
}

export function setCacheBudget(bytes: number): Promise<void> {
  return call<void>({ type: 'SET_BUDGET', bytes });
}

export function evictAlbum(albumId: string): Promise<{ freedBytes: number }> {
  return call<{ freedBytes: number }>({ type: 'EVICT_ALBUM', albumId });
}

export function clearAllCaches(): Promise<void> {
  return call<void>({ type: 'CLEAR_ALL' }, 30000);
}

type Listener = (event: { type: 'progress'; data: PrecacheProgress } | { type: 'done'; data: PrecacheResult }) => void;

export function onPrecacheEvents(listener: Listener): () => void {
  if (!serviceWorkerSupported()) return () => {};

  const handler = (event: MessageEvent) => {
    const data = event.data as { type?: string };
    if (data?.type === 'PRECACHE_PROGRESS') listener({ type: 'progress', data: event.data as PrecacheProgress });
    else if (data?.type === 'PRECACHE_DONE') listener({ type: 'done', data: event.data as PrecacheResult });
  };

  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}
